import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, KeyRound, Eye, EyeOff } from 'lucide-react';
import API from '../lib/api';

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';
  const [pwd, setPwd] = useState('');
  const [pwd2, setPwd2] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!token) return setError('Reset token is missing from the URL.');
    if (pwd.length < 8) return setError('Password must be at least 8 characters.');
    if (pwd !== pwd2) return setError('Passwords do not match.');
    setBusy(true);
    try {
      await API.post('/auth/reset-password', { token, new_password: pwd });
      setDone(true);
      setTimeout(() => navigate('/'), 3500);
    } catch (err) {
      setError(err.response?.data?.detail || 'Reset failed. The link may have expired.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#04160d] flex items-center justify-center p-6">
      <div className="w-full max-w-md rounded-3xl bg-[#0a2416] border border-emerald-500/25 shadow-2xl p-8" data-testid="reset-password-page">
        <div className="text-center">
          <div className="text-emerald-300 font-serif text-2xl mb-1">SkyWay <span className="text-emerald-500">Green</span></div>
          <div className="text-[11px] uppercase tracking-[0.14em] text-emerald-500/80 mb-8">Ecosystem Infrastructure</div>
        </div>

        {done ? (
          <div className="text-center" data-testid="reset-success">
            <div className="mx-auto h-16 w-16 rounded-full bg-emerald-500/10 border border-emerald-500/40 flex items-center justify-center mb-4">
              <CheckCircle2 size={36} className="text-emerald-400" />
            </div>
            <h1 className="font-serif text-2xl text-emerald-50 mb-2">Password Updated</h1>
            <p className="text-sm text-emerald-200/70 mb-6">Redirecting you to the portal — you can now sign in with your new password.</p>
            <Link to="/" className="inline-flex items-center justify-center rounded-full bg-gradient-to-r from-lime-400 to-emerald-400 text-emerald-950 font-semibold px-6 py-3 text-sm">Sign In Now</Link>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div className="text-center mb-4">
              <div className="mx-auto h-14 w-14 rounded-full bg-emerald-500/10 border border-emerald-500/40 flex items-center justify-center mb-3">
                <KeyRound size={26} className="text-emerald-400" />
              </div>
              <h1 className="font-serif text-2xl text-emerald-50">Set a new password</h1>
              <p className="text-xs text-emerald-200/60 mt-1">This link is valid for 1 hour and can only be used once.</p>
            </div>

            <label className="block">
              <span className="block text-[11px] font-semibold uppercase tracking-[0.14em] text-emerald-300 mb-1.5">New Password</span>
              <div className="relative">
                <input
                  type={show ? 'text' : 'password'}
                  required minLength={8}
                  value={pwd}
                  onChange={(e) => setPwd(e.target.value)}
                  className="dark-input w-full pr-10"
                  placeholder="Minimum 8 characters"
                  data-testid="reset-pwd-input"
                />
                <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-emerald-300/70">
                  {show ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </label>

            <label className="block">
              <span className="block text-[11px] font-semibold uppercase tracking-[0.14em] text-emerald-300 mb-1.5">Confirm New Password</span>
              <input
                type={show ? 'text' : 'password'}
                required minLength={8}
                value={pwd2}
                onChange={(e) => setPwd2(e.target.value)}
                className="dark-input w-full"
                placeholder="Retype password"
                data-testid="reset-pwd2-input"
              />
            </label>

            {error && (
              <div className="rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-[13px] text-red-300" data-testid="reset-error">{error}</div>
            )}

            <button
              type="submit"
              disabled={busy}
              className="w-full rounded-full bg-gradient-to-r from-lime-400 to-emerald-400 hover:brightness-110 disabled:opacity-60 text-emerald-950 font-semibold py-3 text-sm transition"
              data-testid="reset-submit-btn"
            >
              {busy ? 'Updating…' : 'Update Password'}
            </button>

            <div className="text-center pt-2">
              <Link to="/" className="text-xs text-emerald-300/70 hover:text-emerald-200">Back to Portal</Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
